<template>
  <div>
    <b-card>
      <b-card-title>
        <feather-icon icon="CalendarIcon" size="20" class="mr-1" />
        Programar Calendario de Evaluaciones
      </b-card-title>

      <!-- Formulario de Selección -->
      <b-row>
        <b-col md="6">
          <b-form-group label="Programación Académica*">
            <v-select
              v-model="selectedProgramacion"
              :options="programaciones"
              label="descripcion"
              placeholder="Seleccione una programación"
              @input="loadAsignaciones"
            >
              <template #option="{ periodoCodigo, carreraNombre, tipoPlanNombre }">
                {{ periodoCodigo }} - {{ carreraNombre }} - {{ tipoPlanNombre }}
              </template>
              <template #selected-option="{ periodoCodigo, carreraNombre, tipoPlanNombre }">
                {{ periodoCodigo }} - {{ carreraNombre }} - {{ tipoPlanNombre }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-alert v-if="!selectedProgramacion" show variant="info" class="mt-2">
        <feather-icon icon="InfoIcon" class="mr-1" />
        Seleccione una programación académica para comenzar a programar las evaluaciones
      </b-alert>

      <!-- Tabla de Cursos con Fechas de Evaluación -->
      <div v-if="selectedProgramacion && cursos.length > 0">
        <hr>
        <h5 class="mb-3">Programar Fechas de Evaluación</h5>
        
        <b-table
          :items="cursos"
          :fields="tableFields"
          responsive
          striped
          class="evaluation-form-table"
        >
          <template #cell(curso)="{ item }">
            <div>
              <strong>{{ item.cursoNombre }}</strong>
              <br>
              <small class="text-muted">{{ item.cursoCodigo }}</small>
            </div>
          </template>

          <template #cell(horario)="{ item }">
            <b-badge variant="info">{{ item.diaSemana }}</b-badge>
            <br>
            <small>{{ item.horaInicio }} - {{ item.horaFin }}</small>
          </template>

          <template #cell(parcial1)="{ item }">
            <b-form-input
              v-model="item.evaluaciones.parcial1"
              type="date"
              size="sm"
            />
          </template>

          <template #cell(extraordinario1)="{ item }">
            <b-form-input
              v-model="item.evaluaciones.extraordinario1"
              type="date"
              size="sm"
            />
          </template>

          <template #cell(parcial2)="{ item }">
            <b-form-input
              v-model="item.evaluaciones.parcial2"
              type="date"
              size="sm"
            />
          </template>

          <template #cell(extraordinario2)="{ item }">
            <b-form-input
              v-model="item.evaluaciones.extraordinario2"
              type="date"
              size="sm"
            />
          </template>

          <template #cell(final)="{ item }">
            <b-form-input
              v-model="item.evaluaciones.final"
              type="date"
              size="sm"
            />
          </template>

          <template #cell(recuperacion)="{ item }">
            <b-form-input
              v-model="item.evaluaciones.recuperacion"
              type="date"
              size="sm"
            />
          </template>
        </b-table>

        <!-- Botones de Acción -->
        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="outline-secondary"
            @click="resetForm"
            class="mr-2"
          >
            <feather-icon icon="XIcon" class="mr-1" />
            Cancelar
          </b-button>
          <b-button
            variant="primary"
            @click="guardarEvaluaciones"
            :disabled="saving"
          >
            <b-spinner v-if="saving" small class="mr-1" />
            <feather-icon v-else icon="SaveIcon" class="mr-1" />
            Guardar Evaluaciones
          </b-button>
        </div>
      </div>

      <b-alert v-else-if="selectedProgramacion && cursos.length === 0" show variant="warning" class="mt-3">
        <feather-icon icon="AlertTriangleIcon" class="mr-1" />
        No se encontraron cursos asignados para esta programación académica
      </b-alert>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BRow, BCol, BFormGroup, BFormInput,
  BTable, BBadge, BButton, BAlert, BSpinner
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import programacionesService from '@/services/programacionesService'
import asignacionesService from '@/services/asignacionesService'
import calendarioEvaluacionService from '@/services/calendarioEvaluacionService'

export default {
  name: 'CalendarioEvaluacionFormView',
  components: {
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BTable,
    BBadge,
    BButton,
    BAlert,
    BSpinner,
    vSelect,
  },
  data() {
    return {
      selectedProgramacion: null,
      programaciones: [],
      cursos: [],
      saving: false,
      
      tableFields: [
        { key: 'curso', label: 'Curso', sortable: true },
        { key: 'horario', label: 'Horario' },
        { key: 'parcial1', label: '1er Parcial' },
        { key: 'extraordinario1', label: 'Extraordinario 1' },
        { key: 'parcial2', label: '2do Parcial' },
        { key: 'extraordinario2', label: 'Extraordinario 2' },
        { key: 'final', label: 'Evaluación Final' },
        { key: 'recuperacion', label: 'Recuperación' },
      ],
    }
  },
  async mounted() {
    await this.loadProgramaciones()
  },
  methods: {
    async loadProgramaciones() {
      try {
        const data = await programacionesService.getAll()
        this.programaciones = data.map(p => ({
          ...p,
          descripcion: `${p.periodoCodigo} - ${p.carreraNombre} - ${p.tipoPlanNombre || 'Sin plan'}`
        }))
      } catch (error) {
        console.error('Error loading programaciones:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Error al cargar las programaciones académicas',
          },
        })
      }
    },

    async loadAsignaciones() {
      if (!this.selectedProgramacion) return

      try {
        // Obtener todas las asignaciones
        const allAsignaciones = await asignacionesService.getAll()
        
        // Filtrar por programación seleccionada
        const asignaciones = allAsignaciones.filter(
          a => a.programacionAcademicaId === this.selectedProgramacion.programacionAcademicaId
        )

        // Agrupar por curso (un curso puede tener múltiples asignaciones/secciones)
        const cursosMap = new Map()
        asignaciones.forEach(asignacion => {
          if (!cursosMap.has(asignacion.cursoId)) {
            cursosMap.set(asignacion.cursoId, {
              cursoId: asignacion.cursoId,
              cursoNombre: asignacion.cursoNombre,
              cursoCodigo: asignacion.cursoId, // O usa un código si está disponible
              diaSemana: asignacion.diaSemana,
              horaInicio: asignacion.horaInicio,
              horaFin: asignacion.horaFin,
              evaluaciones: {
                parcial1: '',
                extraordinario1: '',
                parcial2: '',
                extraordinario2: '',
                final: '',
                recuperacion: '',
              },
            })
          }
        })

        this.cursos = Array.from(cursosMap.values())

        // Cargar evaluaciones existentes si las hay
        await this.loadExistingEvaluaciones()
      } catch (error) {
        console.error('Error loading asignaciones:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Error al cargar las asignaciones',
          },
        })
      }
    },

    async loadExistingEvaluaciones() {
      try {
        const evaluaciones = await calendarioEvaluacionService.getByProgramacion(
          this.selectedProgramacion.programacionAcademicaId
        )

        // Mapear evaluaciones existentes a los cursos
        evaluaciones.forEach(ev => {
          const curso = this.cursos.find(c => c.cursoId === ev.cursoId)
          if (curso) {
            const fecha = ev.fechaEvaluacion.split('T')[0] // Formato YYYY-MM-DD
            
            if (ev.tipoEvaluacion === '1er Parcial') {
              curso.evaluaciones.parcial1 = fecha
            } else if (ev.tipoEvaluacion === 'Extraordinario 1') {
              curso.evaluaciones.extraordinario1 = fecha
            } else if (ev.tipoEvaluacion === '2do Parcial') {
              curso.evaluaciones.parcial2 = fecha
            } else if (ev.tipoEvaluacion === 'Extraordinario 2') {
              curso.evaluaciones.extraordinario2 = fecha
            } else if (ev.tipoEvaluacion === 'Evaluación Final') {
              curso.evaluaciones.final = fecha
            } else if (ev.tipoEvaluacion === 'Recuperación') {
              curso.evaluaciones.recuperacion = fecha
            }
          }
        })
      } catch (error) {
        console.error('Error loading existing evaluaciones:', error)
      }
    },

    async guardarEvaluaciones() {
      this.saving = true
      
      try {
        // Preparar datos para enviar en batch
        const evaluaciones = []

        this.cursos.forEach(curso => {
          const cursoEvaluaciones = []

          if (curso.evaluaciones.parcial1) {
            cursoEvaluaciones.push({
              tipoEvaluacion: '1er Parcial',
              fechaEvaluacion: curso.evaluaciones.parcial1,
              diaSemana: curso.diaSemana,
              horaInicio: curso.horaInicio,
              horaFin: curso.horaFin,
            })
          }

          if (curso.evaluaciones.extraordinario1) {
            cursoEvaluaciones.push({
              tipoEvaluacion: 'Extraordinario 1',
              fechaEvaluacion: curso.evaluaciones.extraordinario1,
              diaSemana: curso.diaSemana,
              horaInicio: curso.horaInicio,
              horaFin: curso.horaFin,
            })
          }

          if (curso.evaluaciones.parcial2) {
            cursoEvaluaciones.push({
              tipoEvaluacion: '2do Parcial',
              fechaEvaluacion: curso.evaluaciones.parcial2,
              diaSemana: curso.diaSemana,
              horaInicio: curso.horaInicio,
              horaFin: curso.horaFin,
            })
          }

          if (curso.evaluaciones.extraordinario2) {
            cursoEvaluaciones.push({
              tipoEvaluacion: 'Extraordinario 2',
              fechaEvaluacion: curso.evaluaciones.extraordinario2,
              diaSemana: curso.diaSemana,
              horaInicio: curso.horaInicio,
              horaFin: curso.horaFin,
            })
          }

          if (curso.evaluaciones.final) {
            cursoEvaluaciones.push({
              tipoEvaluacion: 'Evaluación Final',
              fechaEvaluacion: curso.evaluaciones.final,
              diaSemana: curso.diaSemana,
              horaInicio: curso.horaInicio,
              horaFin: curso.horaFin,
            })
          }

          if (curso.evaluaciones.recuperacion) {
            cursoEvaluaciones.push({
              tipoEvaluacion: 'Recuperación',
              fechaEvaluacion: curso.evaluaciones.recuperacion,
              diaSemana: curso.diaSemana,
              horaInicio: curso.horaInicio,
              horaFin: curso.horaFin,
            })
          }

          if (cursoEvaluaciones.length > 0) {
            evaluaciones.push({
              cursoId: curso.cursoId,
              detalles: cursoEvaluaciones,
            })
          }
        })

        if (evaluaciones.length === 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Advertencia',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
              text: 'No se ingresaron fechas de evaluación',
            },
          })
          return
        }

        // Enviar en batch
        await calendarioEvaluacionService.createBatch({
          programacionAcademicaId: this.selectedProgramacion.programacionAcademicaId,
          evaluaciones,
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Éxito',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Calendario de evaluaciones guardado correctamente',
          },
        })

        // Recargar datos
        await this.loadAsignaciones()
      } catch (error) {
        console.error('Error saving evaluaciones:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Error al guardar el calendario de evaluaciones',
          },
        })
      } finally {
        this.saving = false
      }
    },

    resetForm() {
      this.selectedProgramacion = null
      this.cursos = []
    },
  },
}
</script>

<style scoped>
@import "~vue-select/dist/vue-select.css";
.evaluation-form-table >>> th {
  font-size: 0.875rem;
  white-space: nowrap;
}

.evaluation-form-table >>> td {
  vertical-align: middle;
}

.evaluation-form-table >>> input[type="date"] {
  font-size: 0.875rem;
}
</style>

